import React, { useState, useEffect, useMemo } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { CircularProgress, IconButton } from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { red } from "@mui/material/colors";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";
import api from "core/interceptors/api";
import LogoutButton from "layouts/logout";
import { useNavigate } from "react-router-dom";

const EMPTY_FORM = { title: "", content: "", version: "" };

export const TermsAndConditions = () => {
  const [termsList, setTermsList] = useState([]);
  const [filteredTerms, setFilteredTerms] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [formLoading, setFormLoading] = useState(false);

  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const [editInput, setEditInput] = useState(EMPTY_FORM);
  const [deleteTermsId, setDeleteTermsId] = useState(null);
  const [errors, setErrors] = useState({});

  /* Snackbar */
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const navigate = useNavigate();

  /* ================= SNACKBAR ================= */
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  /* ================= FETCH ================= */
  useEffect(() => {
    fetchTerms();
  }, []);

  const fetchTerms = async () => {
    setLoading(true);
    try {
      const res = await api.get("/terms");
      if (res.data.status === 1) {
        setTermsList(res.data.terms || []);
      }
    } catch {
      showSnackbar("Failed to load terms", "error");
    } finally {
      setLoading(false);
    }
  };

  /* ================= SEARCH ================= */
  useEffect(() => {
    const q = searchQuery.toLowerCase();
    setFilteredTerms(
      termsList.filter(t => t.title?.toLowerCase().includes(q))
    );
    setPage(0);
  }, [searchQuery, termsList]);

  /* ================= HANDLERS ================= */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditInput(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: "" }));
  };

  const handlePageChange = (event, newPage) => setPage(newPage);

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCloseModal = () => {
    setOpenAddModal(false);
    setOpenEditModal(false);
    setOpenDeleteModal(false);
    setEditInput(EMPTY_FORM);
    setDeleteTermsId(null);
    setErrors({});
  };

  /* ================= VALIDATION ================= */
  const validateForm = () => {
    const newErrors = {};

    if (!editInput.title.trim()) {
      newErrors.title = "Title is required";
    }

    if (!editInput.content.trim()) {
      newErrors.content = "Content is required";
    }

    if (!editInput.id) {
      if (!editInput.version || isNaN(editInput.version)) {
        newErrors.version = "Version must be a valid number";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* ================= SAVE ================= */
  const saveTerms = async () => {
    if (!validateForm()) return;

    setFormLoading(true);
    try {
      if (editInput.id) {
        if (!editInput.is_active) {
          showSnackbar("Only active terms can be edited", "warning");
          return;
        }
        await api.put(`/terms/${editInput.id}`, editInput);
        showSnackbar("Terms updated successfully", "success");
      } else {
        await api.post("/terms", editInput);
        showSnackbar("New Terms version created", "success");
      }

      handleCloseModal();
      fetchTerms();
    } catch (err) {
      showSnackbar(err.response?.data?.msg || "Unexpected error", "error");
    } finally {
      setFormLoading(false);
    }
  };

  /* ================= DELETE (BLOCKED) ================= */
  const deleteTerms = async () => {
    showSnackbar("Terms & Conditions history cannot be deleted", "warning");
    handleCloseModal();
  };

  /* ================= PAGINATION ================= */
  const paginatedData = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredTerms.slice(start, start + rowsPerPage);
  }, [filteredTerms, page, rowsPerPage]);

  /* ================= TABLE ================= */
  const columns = useMemo(() => [
    {
      Header: "S.No",
      align: "center",
      Cell: ({ row }) => page * rowsPerPage + row.index + 1
    },
    { Header: "Title", accessor: "title" },
    { Header: "Version", accessor: "version", align: "center" },
    {
      Header: "Options",
      align: "center",
      Cell: ({ row }) => (
        <MDBox display="flex" justifyContent="center">
          {row.original.is_active && (
            <IconButton
              size="small"
              sx={{ color: "#2e7d32" }}
              onClick={() => {
                setEditInput(row.original);
                setOpenEditModal(true);
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          )}

          <IconButton
            size="small"
            sx={{ color: red[700] }}
            onClick={() => {
              setDeleteTermsId(row.original.id);
              setOpenDeleteModal(true);
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </MDBox>
      )
    }
  ], [page, rowsPerPage]);

  /* ================= RENDER ================= */
  return (
    <DashboardLayout>
      <MDBox display="flex" justifyContent="flex-end">
        <LogoutButton onLogout={() => navigate("/login")} />
      </MDBox>

      <DashboardNavbar />

      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                display="flex"
                justifyContent="space-between"
              >
                <MDTypography color="white">Terms & Conditions</MDTypography>
                <MDButton
                  color="success"
                  onClick={() => {
                    setEditInput(EMPTY_FORM);
                    setErrors({});
                    setOpenAddModal(true);
                  }}
                >
                  Add Terms
                </MDButton>
              </MDBox>

              <MDBox display="flex" justifyContent="flex-end" p={2}>
                <TextField
                  size="small"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </MDBox>

              <MDBox p={2}>
                {loading ? (
                  <CircularProgress />
                ) : (
                  <DataTable
                    table={{ columns, rows: paginatedData }}
                    isSorted
                    entriesPerPage={{
                      defaultValue: rowsPerPage,
                      entries: [5, 10, 15, 20],
                    }}
                    showTotalEntries
                    noEndBorder
                    pagination={{
                      page,
                      rowsPerPage,
                      onPageChange: handlePageChange,
                      onRowsPerPageChange: handleRowsPerPageChange,
                      count: filteredTerms.length,
                    }}
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>

        {/* ADD / EDIT MODAL */}
        <Dialog open={openAddModal || openEditModal} onClose={handleCloseModal} fullWidth maxWidth="md">
          <DialogTitle>{editInput.id ? "Edit Terms" : "Add Terms"}</DialogTitle>
          <DialogContent dividers>
            <TextField
              label="Title"
              name="title"
              fullWidth
              required
              value={editInput.title}
              onChange={handleInputChange}
              error={Boolean(errors.title)}
              helperText={errors.title}
            />

            <TextField
              label="Content"
              name="content"
              fullWidth
              required
              multiline
              minRows={8}
              sx={{ mt: 2 }}
              value={editInput.content}
              onChange={handleInputChange}
              error={Boolean(errors.content)}
              helperText={errors.content}
            />

            <TextField
              label="Version"
              name="version"
              fullWidth
              sx={{ mt: 2 }}
              value={editInput.version}
              onChange={handleInputChange}
              InputProps={{ readOnly: Boolean(editInput.id) }}
              error={Boolean(errors.version)}
              helperText={
                errors.version ||
                (editInput.id ? "Version cannot be changed once created" : "")
              }
            />
          </DialogContent>

          <DialogActions>
            <MDButton onClick={handleCloseModal}>Cancel</MDButton>
            <MDButton color="success" onClick={saveTerms} disabled={formLoading}>
              {formLoading ? <CircularProgress size={22} /> : editInput.id ? "Update" : "Add"}
            </MDButton>
          </DialogActions>
        </Dialog>

        {/* DELETE MODAL */}
        <Dialog open={openDeleteModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
          <DialogTitle>
            <WarningIcon sx={{ color: red[700], fontSize: 40 }} /> Delete Not Allowed
          </DialogTitle>
          <DialogContent dividers>
            <MDTypography>
              Terms & Conditions history cannot be deleted.
            </MDTypography>
          </DialogContent>
          <DialogActions>
            <MDButton onClick={handleCloseModal}>Close</MDButton>
          </DialogActions>
        </Dialog>

        {/* SNACKBAR */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            severity={snackbar.severity}
            sx={{ width: "100%" }}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </MDBox>
    </DashboardLayout>
  );
};

export default TermsAndConditions;
